import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import AICustomerBudgets, { BudgetStatus } from "../components/AICustomerBudgets";
import AIMemoryManager from "../components/AIMemoryManager";

type Provider = {
  id: number;
  provider: string;
  display_name: string | null;
  default_model: string;
  api_base_url: string | null;
  extra_config: Record<string, any>;
  is_default: boolean;
  is_active: boolean;
  has_api_key: boolean;
  last_test_at: string | null;
  last_test_result: string | null;
  last_test_error: string | null;
};

type Flags = {
  ai_enabled: boolean;
  ai_ticket_enabled: boolean;
  ai_action_enabled: boolean;
  ai_alert_enabled: boolean;
  ai_alert_ticket_autocreate: boolean;
  ai_compliance_enabled: boolean;
  ai_terminal_enabled: boolean;
  ai_default_provider_id: number | null;
};

type Usage = {
  days: number;
  total: { calls: number; input_tokens: number; output_tokens: number; cost_usd: number };
  by_provider: { key: string; calls: number; input_tokens: number; output_tokens: number; cost_usd: number }[];
  by_day: { date: string; calls: number; cost_usd: number }[];
};

const PROVIDER_KINDS = [
  { value: "anthropic", label: "Anthropic Claude", defaultModel: "claude-opus-4-7" },
  { value: "openai", label: "OpenAI", defaultModel: "gpt-4o" },
  {
    value: "azure_openai",
    label: "Azure OpenAI",
    defaultModel: "gpt-4o",
    needsBaseUrl: true,
  },
  { value: "google", label: "Google Gemini", defaultModel: "gemini-2.0-flash" },
  {
    value: "ollama",
    label: "Ollama (local)",
    defaultModel: "llama3.1",
    needsBaseUrl: true,
    optionalKey: true,
  },
  {
    value: "custom",
    label: "Custom (OpenAI-compatible)",
    defaultModel: "gpt-4o",
    needsBaseUrl: true,
  },
];

export default function AISettings() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [flags, setFlags] = useState<Flags | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  // Monthly ceiling + month-to-date spend. Distinct from `usage`, which is
  // historical charting; this answers "how much is left, and is anything paused".
  const [budget, setBudget] = useState<BudgetStatus | null>(null);
  const [budgetInput, setBudgetInput] = useState<string>("");
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const usageSort = useSort("cost_usd", "desc");
  const providerSort = useSort("provider", "asc");

  const refresh = async () => {
    setBusy(true);
    try {
      // allSettled, NOT all. Promise.all rejects on the first failure and
      // discards the other three responses - so a blip on /usage or /budget,
      // neither of which drives a toggle, used to throw away a perfectly good
      // /flags response and leave every AI switch rendering OFF.
      const [p, f, u, b] = await Promise.allSettled([
        api.get("/api/v1/ai/providers"),
        api.get("/api/v1/ai/flags"),
        api.get("/api/v1/ai/usage", { params: { days: 30 } }),
        api.get("/api/v1/ai/budget"),
      ]);
      if (p.status === "fulfilled") setProviders(p.value.data);
      if (f.status === "fulfilled") setFlags(f.value.data);
      if (u.status === "fulfilled") setUsage(u.value.data);
      if (b.status === "fulfilled") {
        setBudget(b.value.data);
        setBudgetInput(
          b.value.data?.budget_usd ? String(b.value.data.budget_usd) : ""
        );
      }
      // Name what failed. "Request failed with status code 502" on its own
      // told the user nothing about which part of the page they could still
      // trust, and the part they could not trust was the one that mattered.
      const failed = [
        p.status === "rejected" && "providers",
        f.status === "rejected" && "AI settings",
        u.status === "rejected" && "usage",
        b.status === "rejected" && "spend limit",
      ].filter(Boolean) as string[];
      setError(
        failed.length
          ? `Could not load: ${failed.join(", ")}. Nothing has been changed \u2014 ` +
            `this is a display problem, not a settings change.`
          : null
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const updateFlag = async (k: keyof Flags, v: any) => {
    if (!flags) return;
    const body = { [k]: v };
    try {
      const r = await api.put("/api/v1/ai/flags", body);
      setFlags(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Update failed");
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm("Delete this AI provider config?")) return;
    await api.delete(`/api/v1/ai/providers/${id}`);
    await refresh();
  };

  const testSaved = async (id: number) => {
    setBusy(true);
    try {
      const r = await api.post(`/api/v1/ai/providers/${id}/test`);
      await refresh();
      alert(r.data.ok ? "✓ " + r.data.message : "✗ " + r.data.message);
    } catch (e: any) {
      alert("Test failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">AI Settings</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure AI providers and the global AI mode toggle. API keys are encrypted at rest
          with <code>AI_PROVIDER_KEY</code>; the master key never leaves the server.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded text-sm flex items-start justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={refresh}
            disabled={busy}
            className="shrink-0 px-2 py-0.5 rounded border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/60 disabled:opacity-50"
          >
            {busy ? "Retrying\u2026" : "Retry"}
          </button>
        </div>
      )}

      {/* Master mode + feature toggles.

        RENDERED ONLY WHEN THE STATE IS KNOWN. A switch is a claim about the
        system, and drawing it OFF because the fetch failed is a lie the user
        cannot tell from the truth - which is precisely what happened: every AI
        toggle showed DISABLED after a transient error and the owner of the
        system believed someone had switched AI off. If the state cannot be
        read, say so and show nothing to act on. */}
      {!flags ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-500 dark:text-gray-400">
          {busy
            ? "Loading AI settings\u2026"
            : "AI settings could not be read, so they are not shown here. They have not changed \u2014 use Retry above."}
        </div>
      ) : (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">AI Mode</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Master switch. When OFF, the AI subsystem is completely inert — no calls go out,
              no actions get proposed, even if individual features below are enabled.
            </p>
          </div>
          <Toggle
            on={!!flags?.ai_enabled}
            onChange={(v) => updateFlag("ai_enabled", v)}
            label={flags?.ai_enabled ? "ENABLED" : "DISABLED"}
          />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
          <FeatureToggle
            on={!!flags?.ai_ticket_enabled}
            disabled={!flags?.ai_enabled}
            label="Ticket Assist"
            desc="Summarize threads, suggest replies, classify priority/category"
            onChange={(v) => updateFlag("ai_ticket_enabled", v)}
          />
          <FeatureToggle
            on={!!flags?.ai_alert_enabled}
            disabled={!flags?.ai_enabled}
            label="Alert Summarization"
            desc="Plain-English alert explanations + suggested next action"
            onChange={(v) => updateFlag("ai_alert_enabled", v)}
          />
          <FeatureToggle
            on={!!flags?.ai_action_enabled}
            disabled={!flags?.ai_enabled}
            label="Action Engine"
            desc="AI proposes repairs against devices. Approval required for medium/high risk."
            onChange={(v) => updateFlag("ai_action_enabled", v)}
          />
          <FeatureToggle
            on={!!flags?.ai_alert_ticket_autocreate}
            disabled={!flags?.ai_enabled}
            label="Auto-ticket from alerts"
            desc="On every NEW critical alert, AI drafts a ticket. Queue in AI Actions until approved."
            onChange={(v) => updateFlag("ai_alert_ticket_autocreate", v)}
          />
          <FeatureToggle
            on={!!flags?.ai_compliance_enabled}
            disabled={!flags?.ai_enabled}
            label="Compliance assessments"
            desc="HIPAA / SOX / GLBA / SOC 2 / PCI / NIST CSF / CIS — AI scores controls against current RMM posture."
            onChange={(v) => updateFlag("ai_compliance_enabled", v)}
          />
          <FeatureToggle
            on={!!flags?.ai_terminal_enabled}
            disabled={!flags?.ai_enabled}
            label="AI Terminal"
            desc="In-app bash terminal on the appliance with CLAUDE.md pre-staged so Claude knows it's inside the RMM."
            onChange={(v) => updateFlag("ai_terminal_enabled", v)}
          />
        </div>
      </div>
      )}

      {/* Device advisor — proactive recommendations */}
      <DeviceAdvisorCard />

      {/* MCP info — external AI clients */}
      <MCPInfoCard />

      {/* Monthly spend ceiling */}
      {budget && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-2xl">
              <h2 className="font-semibold">Monthly spend limit</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                A ceiling on what AeroGauge may spend against your AI key each calendar month,
                across every customer. Leave it blank for no limit.
              </p>
              {/* The design decision worth stating in the UI, because it is the
                  difference between a useful cap and one that causes an outage. */}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                At the limit, <strong>automatic</strong> AI pauses — alert triage, auto-created
                tickets, background summaries. The assistant stays available to technicians, so
                nobody is blocked mid-incident by a limit a nightly job used up.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">$</span>
              <input
                type="number"
                min={0}
                step={10}
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                placeholder="no limit"
                className="w-28 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">/ month</span>
              <button
                onClick={async () => {
                  setBudgetSaving(true);
                  try {
                    const v = parseFloat(budgetInput);
                    const r = await api.put("/api/v1/ai/budget", {
                      monthly_budget_usd: Number.isFinite(v) && v > 0 ? v : 0,
                    });
                    setBudget(r.data);
                    setError(null);
                  } catch (e: any) {
                    setError(e.response?.data?.detail || "Couldn't save the limit");
                  } finally {
                    setBudgetSaving(false);
                  }
                }}
                disabled={budgetSaving}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg"
              >
                {budgetSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-700 dark:text-gray-300">
                ${budget.spent_usd.toFixed(2)} spent across all customers this month
                {budget.enforced && <> of ${budget.budget_usd.toFixed(2)}</>}
              </span>
              {budget.enforced && (
                <span
                  className={
                    budget.automatic_paused
                      ? "text-red-600 dark:text-red-400 font-medium"
                      : budget.percent_used >= budget.warn_percent
                        ? "text-amber-600 dark:text-amber-400 font-medium"
                        : "text-gray-500 dark:text-gray-400"
                  }
                >
                  {budget.percent_used.toFixed(0)}%
                </span>
              )}
            </div>
            {budget.enforced && (
              <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className={
                    budget.automatic_paused
                      ? "h-full bg-red-500"
                      : budget.percent_used >= budget.warn_percent
                        ? "h-full bg-amber-500"
                        : "h-full bg-emerald-500"
                  }
                  style={{ width: `${Math.min(100, budget.percent_used)}%` }}
                />
              </div>
            )}
            {budget.automatic_paused && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                Automatic AI is paused for the rest of this month. Technicians can still use the
                assistant. Raise the limit above to resume automation.
              </p>
            )}
            {!budget.enforced && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                No platform limit set — total spend is unbounded.
              </p>
            )}
          </div>

          <AICustomerBudgets budget={budget} onChange={setBudget} onError={setError} />
        </div>
      )}

      {/* What the assistant has been taught. Placed on AI Settings rather than
          buried per-customer because it is a platform-level thing an operator
          reviews as a whole — including the house rules that apply to every
          client. */}
      <AIMemoryManager />

      {/* Usage + cost card */}
      {usage && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-semibold">Usage (last {usage.days} days)</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Token counts and estimated cost. Update prices in{" "}
                <code>services/ai/usage.py</code> for accurate accounting.
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">
                ${usage.total.cost_usd.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {usage.total.calls.toLocaleString()} calls ·{" "}
                {(
                  (usage.total.input_tokens + usage.total.output_tokens) /
                  1000
                ).toFixed(1)}
                k tokens
              </div>
            </div>
          </div>
          {usage.by_provider.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="Provider · Model" sortKey="key" sort={usageSort.sort} onSort={usageSort.toggle} />
                  <SortableTh label="Calls" sortKey="calls" sort={usageSort.sort} onSort={usageSort.toggle} align="right" />
                  <SortableTh label="Input tokens" sortKey="input_tokens" sort={usageSort.sort} onSort={usageSort.toggle} align="right" />
                  <SortableTh label="Output tokens" sortKey="output_tokens" sort={usageSort.sort} onSort={usageSort.toggle} align="right" />
                  <SortableTh label="Cost" sortKey="cost_usd" sort={usageSort.sort} onSort={usageSort.toggle} align="right" />
                </tr>
              </thead>
              <tbody>
                {sortRows(usage.by_provider, usageSort.sort, (p, key) => {
                  switch (key) {
                    case "calls": return p.calls;
                    case "input_tokens": return p.input_tokens;
                    case "output_tokens": return p.output_tokens;
                    case "cost_usd": return p.cost_usd;
                    default: return (p.key || "").toLowerCase();
                  }
                }).map((p) => (
                  <tr key={p.key} className="border-b dark:border-gray-700 last:border-0">
                    <td className="py-1 font-mono text-xs">{p.key}</td>
                    <td className="py-1 text-right font-mono text-xs">{p.calls}</td>
                    <td className="py-1 text-right font-mono text-xs">
                      {p.input_tokens.toLocaleString()}
                    </td>
                    <td className="py-1 text-right font-mono text-xs">
                      {p.output_tokens.toLocaleString()}
                    </td>
                    <td className="py-1 text-right font-mono text-xs">
                      ${p.cost_usd.toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
              No AI calls yet. Run a ticket assist or device diagnose to see usage here.
            </div>
          )}
        </div>
      )}

      {/* Provider configs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-semibold">Providers</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Add the providers your MSP wants to use. The default-flagged provider handles
              every AI call unless overridden.
            </p>
          </div>
          <button
            onClick={() => {
              setEditingId(null);
              setShowCreate(true);
            }}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
          >
            + Add provider
          </button>
        </div>

        {providers.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
            No providers configured. Add one to enable AI features.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
              <tr>
                <SortableTh label="Provider" sortKey="provider" sort={providerSort.sort} onSort={providerSort.toggle} />
                <SortableTh label="Display name" sortKey="display_name" sort={providerSort.sort} onSort={providerSort.toggle} />
                <SortableTh label="Model" sortKey="default_model" sort={providerSort.sort} onSort={providerSort.toggle} />
                <SortableTh label="Default" sortKey="is_default" sort={providerSort.sort} onSort={providerSort.toggle} />
                <SortableTh label="Active" sortKey="is_active" sort={providerSort.sort} onSort={providerSort.toggle} />
                <SortableTh label="Last test" sortKey="last_test_result" sort={providerSort.sort} onSort={providerSort.toggle} />
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortRows(providers, providerSort.sort, (p, key) => {
                switch (key) {
                  case "display_name": return (p.display_name || "").toLowerCase();
                  case "default_model": return (p.default_model || "").toLowerCase();
                  case "is_default": return p.is_default ? 1 : 0;
                  case "is_active": return p.is_active ? 1 : 0;
                  case "last_test_result": return (p.last_test_result || "").toLowerCase();
                  default: return (p.provider || "").toLowerCase();
                }
              }).map((p) => (
                <tr key={p.id} className="border-b dark:border-gray-700 last:border-0">
                  <td className="py-2 font-mono text-xs">{p.provider}</td>
                  <td className="py-2">{p.display_name || "—"}</td>
                  <td className="py-2 font-mono text-xs">{p.default_model}</td>
                  <td className="py-2">{p.is_default ? "✓" : ""}</td>
                  <td className="py-2">{p.is_active ? "✓" : ""}</td>
                  <td className="py-2 text-xs">
                    {p.last_test_result === "success" ? (
                      <span className="text-green-700 dark:text-green-400">✓ OK</span>
                    ) : p.last_test_result === "failure" ? (
                      <span className="text-red-700 dark:text-red-400" title={p.last_test_error || ""}>
                        ✗ Failed
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">not tested</span>
                    )}
                  </td>
                  <td className="py-2 text-right space-x-2">
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => testSaved(p.id)}
                      disabled={busy}
                    >
                      Test
                    </button>
                    <button
                      className="text-xs text-gray-700 dark:text-gray-300 hover:underline"
                      onClick={() => {
                        setEditingId(p.id);
                        setShowCreate(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => remove(p.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <ProviderModal
          providers={providers}
          editing={editingId ? providers.find((p) => p.id === editingId) || null : null}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function MCPInfoCard() {
  const [info, setInfo] = useState<{
    name: string;
    protocol: string;
    endpoint: string;
    auth: string;
    tools: string[];
  } | null>(null);
  useEffect(() => {
    api
      .get("/api/v1/mcp/info")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, []);
  if (!info) return null;
  const fullEndpoint = `${window.location.origin}${info.endpoint}`;
  const exampleConfig = `{
  "mcpServers": {
    "aerogauge-rmm": {
      "transport": "http",
      "url": "${fullEndpoint}",
      "headers": {
        "Authorization": "Bearer <your-api-token>"
      }
    }
  }
}`;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="mb-2">
        <h2 className="font-semibold">Model Context Protocol (MCP) — external AI integration</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Connect Claude Desktop, Cursor, or any MCP-compatible AI client to AeroGauge RMM so
          the AI can query devices, tickets, and runbooks — and propose actions through the
          same approval queue.
        </p>
      </div>
      <div className="space-y-2 text-sm">
        <div>
          <strong>Endpoint:</strong>{" "}
          <code className="bg-gray-50 dark:bg-gray-900/50 px-1 rounded text-xs">{fullEndpoint}</code>
        </div>
        <div>
          <strong>Auth:</strong> Bearer token from Integrations → Inbound API access.
        </div>
        <div>
          <strong>Tools exposed:</strong>{" "}
          <span className="text-xs text-gray-600 dark:text-gray-400">{info.tools.join(", ")}</span>
        </div>
      </div>
      <details className="mt-3">
        <summary className="text-xs text-blue-600 cursor-pointer hover:underline">
          Example client config (Claude Desktop / Cursor mcpServers JSON)
        </summary>
        <pre className="text-xs bg-gray-50 dark:bg-gray-900/50 p-2 rounded mt-1 overflow-x-auto">
          {exampleConfig}
        </pre>
      </details>
    </div>
  );
}

function DeviceAdvisorCard() {
  const [busy, setBusy] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runNow = async () => {
    if (
      !window.confirm(
        "Run the AI advisor against every device now? This makes one AI call per device — " +
          "watch the Usage card to see the cost."
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(
        `/api/v1/ai/device-advisor/run-all?only_active=${onlyActive ? "true" : "false"}`
      );
      setLastResult(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h2 className="font-semibold">Device Advisor</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Proactive AI scan of every device. Surfaces 1-3 recommendations per device on the
            Device Detail page. Runs daily at 04:00 ET. Click below to run on-demand.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
            />
            Online only
          </label>
          <button
            onClick={runNow}
            disabled={busy}
            className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Running…" : "Run advisor now"}
          </button>
        </div>
      </div>
      {error && (
        <div className="text-xs bg-red-50 dark:bg-red-900/40 text-red-800 dark:text-red-300 px-2 py-1 rounded">{error}</div>
      )}
      {lastResult && (
        <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
          Last run: analyzed {lastResult.analyzed}, skipped {lastResult.skipped}
          {lastResult.errors?.length > 0 && (
            <span className="text-red-600 dark:text-red-400 ml-2">errors: {lastResult.errors.length}</span>
          )}
        </div>
      )}
    </div>
  );
}

function Toggle({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={() => !disabled && onChange(!on)}
      className={`relative inline-flex h-7 w-14 items-center rounded-full transition ${
        on ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
      aria-pressed={on}
      disabled={disabled}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition shadow ${
          on ? "translate-x-8" : "translate-x-1"
        }`}
      />
      {label && (
        <span className="absolute -bottom-5 right-0 text-xs font-mono text-gray-500 dark:text-gray-400">
          {label}
        </span>
      )}
    </button>
  );
}

function FeatureToggle({
  on,
  disabled,
  label,
  desc,
  onChange,
}: {
  on: boolean;
  disabled?: boolean;
  label: string;
  desc: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      className={`p-3 rounded border ${
        disabled ? "bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700" : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-sm">{label}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{desc}</div>
        </div>
        <Toggle on={on} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
}

function ProviderModal({
  providers,
  editing,
  onClose,
  onSaved,
}: {
  providers: Provider[];
  editing: Provider | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [provider, setProvider] = useState(editing?.provider || "anthropic");
  const [displayName, setDisplayName] = useState(editing?.display_name || "");
  const [model, setModel] = useState(editing?.default_model || "claude-opus-4-7");
  const [apiKey, setApiKey] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState(editing?.api_base_url || "");
  const [extraConfig, setExtraConfig] = useState<string>(
    JSON.stringify(editing?.extra_config || {}, null, 2)
  );
  const [isDefault, setIsDefault] = useState(editing?.is_default || providers.length === 0);
  const [isActive, setIsActive] = useState(editing?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  const kind = PROVIDER_KINDS.find((k) => k.value === provider);

  const onChangeKind = (v: string) => {
    setProvider(v);
    const k = PROVIDER_KINDS.find((p) => p.value === v);
    if (k && !editing) setModel(k.defaultModel);
  };

  const buildPayload = () => {
    let extra = {};
    try {
      extra = JSON.parse(extraConfig || "{}");
    } catch {
      throw new Error("Extra config must be valid JSON");
    }
    const body: any = {
      provider,
      display_name: displayName || null,
      default_model: model,
      api_base_url: apiBaseUrl || null,
      extra_config: extra,
      is_default: isDefault,
      is_active: isActive,
    };
    if (apiKey) body.api_key = apiKey;
    return body;
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const body = buildPayload();
      const r = await api.post("/api/v1/ai/providers/test", {
        provider,
        api_key: apiKey || undefined,
        api_base_url: apiBaseUrl || undefined,
        extra_config: body.extra_config,
        model,
      });
      setTestResult(r.data.ok ? "✓ " + r.data.message : "✗ " + r.data.message);
    } catch (e: any) {
      setTestResult("✗ " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = buildPayload();
      if (editing) {
        await api.put(`/api/v1/ai/providers/${editing.id}`, body);
      } else {
        await api.post("/api/v1/ai/providers", body);
      }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-3">
          {editing ? "Edit provider" : "Add AI provider"}
        </h3>

        <div className="space-y-3">
          <Field label="Provider">
            <select
              value={provider}
              onChange={(e) => onChangeKind(e.target.value)}
              disabled={!!editing}
              className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded px-2 py-1.5 text-sm"
            >
              {PROVIDER_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Display name (optional)">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded px-2 py-1.5 text-sm"
              placeholder="e.g. Production Claude key"
            />
          </Field>

          <Field label="Default model">
            <input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
              placeholder={kind?.defaultModel}
            />
          </Field>

          <Field label={`API key ${kind?.optionalKey ? "(optional for local Ollama)" : ""}`}>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
              placeholder={editing ? "(leave blank to keep existing)" : "sk-..."}
            />
          </Field>

          {(kind?.needsBaseUrl || apiBaseUrl) && (
            <Field
              label={
                provider === "azure_openai"
                  ? "Azure resource endpoint"
                  : provider === "ollama"
                    ? "Ollama URL (default http://localhost:11434)"
                    : "Base URL"
              }
            >
              <input
                value={apiBaseUrl}
                onChange={(e) => setApiBaseUrl(e.target.value)}
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
                placeholder={
                  provider === "azure_openai"
                    ? "https://my-resource.openai.azure.com"
                    : provider === "ollama"
                      ? "http://localhost:11434"
                      : "https://api.openrouter.ai/api/v1"
                }
              />
            </Field>
          )}

          {provider === "azure_openai" && (
            <Field label="Extra config — needs api_version + deployment">
              <textarea
                value={extraConfig}
                onChange={(e) => setExtraConfig(e.target.value)}
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono h-24"
                placeholder='{"api_version": "2024-06-01", "deployment": "my-gpt4o"}'
              />
            </Field>
          )}

          <div className="flex gap-4 text-sm pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              Use as default provider
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </label>
          </div>

          {testResult && (
            <div
              className={`text-xs p-2 rounded ${
                testResult.startsWith("✓")
                  ? "bg-green-50 dark:bg-green-900/40 text-green-800 dark:text-green-300"
                  : "bg-red-50 dark:bg-red-900/40 text-red-800 dark:text-red-300"
              }`}
            >
              {testResult}
            </div>
          )}

          {error && (
            <div className="text-xs p-2 rounded bg-red-50 dark:bg-red-900/40 text-red-800 dark:text-red-300">{error}</div>
          )}
        </div>

        <div className="flex justify-between mt-5 gap-2">
          <button
            onClick={test}
            disabled={busy}
            className="px-3 py-1.5 border dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Test connection
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 border dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={busy}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-50"
            >
              {editing ? "Save" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      {children}
    </div>
  );
}
